'use client';

import { useState, useEffect } from 'react';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { Stack, Typography, Box, Link } from '@mui/material';
import { NumberInput } from '@qb/frontend/components/NumberInput/NumberInput';
import { WarrantyLabel, PriceFields, SubTotal, ProductAndManufacturerNames } from '@qb/frontend/components/TrustBoxAtomComponents/TrustBoxAtomComponents';
import { useIsMobile } from '@qb/frontend/hooks/mediaQueries';
import { calculateDefaultPurchaseQuantityValue } from '@/global/pages/PartDisplayPage/_utils/calculateDefaultPurchaseQuantityValue';
import { calculateNumberInputIncrement } from '@/global/pages/PartDisplayPage/_utils/calculateNumberInputIncrement';
import { useRequestPartPriceFromSPW } from '@/hooks/useRequestPartPriceFromSPW';
import { TenantConfig } from '@/shared/types/controllers/sps/SPSAppControllerTypes';
import { PartDisplayDataResponse } from '@/shared/types/controllers/sps/SPSPartControllerTypes';
import { CallToActionAddToCart } from '../CallToActionAddToCart';
import { CallToActionRequestQuote } from '../CallToActionRequestQuote';
import { TrustBoxPriceDetailsDesktopTrustBoxNavbar } from './_components/TrustBoxPriceDetailsDesktopTrustBoxNavbar';
import { TrustBoxPriceDetailsMobileCallToActionBar } from './_components/TrustBoxPriceDetailsMobileCallToActionBar';
import { TrustBoxPriceDetailsShipWithinLabel } from './_components/TrustBoxPriceDetailsShipWithinLabel';
import { TrustBoxPriceDetailsStockLabel } from './_components/TrustBoxPriceDetailsStockLabel';
type TrustBoxPriceDetailsProps = {
  tenantConfig: TenantConfig;
  part: PartDisplayDataResponse['part'];
  publicQtyAndPrice: PartDisplayDataResponse['publicQtyAndPrice'];
  spPartPageSetting: PartDisplayDataResponse['spPartPageSetting'];
  isNavbarStyleOnDesktop?: boolean;
};
export const TrustBoxPriceDetails = ({
  tenantConfig,
  part,
  publicQtyAndPrice,
  spPartPageSetting,
  isNavbarStyleOnDesktop = false
}: TrustBoxPriceDetailsProps) => {
  const {
    authUser,
    requestMultiplePartsPrices,
    getPartPrice
  } = useRequestPartPriceFromSPW();
  const authUserCompanyName = authUser?.company?.name;
  useEffect(() => {
    /**
     * Request the prices for the PDP part.
     */
    requestMultiplePartsPrices([part.id]);
  }, [part.id, requestMultiplePartsPrices]);
  const {
    onHandQty,
    manufacturerStock,
    price,
    originalPrice,
    shipProcessingDays
  } = {
    ...publicQtyAndPrice,
    ...getPartPrice(part.id)
  };
  const combinedStock = onHandQty + manufacturerStock;
  const hasPrice = !!price;
  const isMarketablePart = combinedStock > 0 && hasPrice;
  const isNoPriceNoAvailability = combinedStock === 0 && !hasPrice;
  const isMobile = useIsMobile();
  const [qty, setQty] = useState(calculateDefaultPurchaseQuantityValue(publicQtyAndPrice));
  const numberInputIncrement = calculateNumberInputIncrement({
    publicQtyAndPrice,
    purchaseQty: qty,
    hasPrice
  });
  const minOrderQtyMessage = numberInputIncrement > 1 && <Typography variant="bodyMedium" sx={{
    mb: 2,
    mt: -1,
    color: 'text.primary'
  }}>
      Minimum order quantity for this item is{' '}
      {publicQtyAndPrice.minimumOrderQty}.
    </Typography>;
  const numberInput = <Stack direction={isNoPriceNoAvailability ? 'row' : 'column'} sx={{
    gap: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }}>
      {!isMobile && <Typography variant="bodyMediumPrimary" sx={{
      color: 'text.primary'
    }}>
          Select Qty:
        </Typography>}
      <NumberInput minValue={numberInputIncrement} step={numberInputIncrement} onValueChange={setQty} initialValue={qty} />
    </Stack>;
  const productAndManufacturerNames = <ProductAndManufacturerNames productName={part.partNumber} manufacturerName={part?.manufacturer?.name} />;
  const yourPriceCanBeLower = !!authUser ? null : <Stack direction="row" sx={{
    bgcolor: 'accent.main',
    mx: -2,
    px: 2,
    py: 0.5,
    mb: 2,
    gap: 1,
    alignItems: 'center'
  }}>
      <NewReleasesIcon />
      <Typography variant="bodySmallPrimary" sx={{
      color: 'text.primary'
    }}>
        Your price can be lower.
        <br />
        <Link href="#" underline="always" onClick={e => {
        e.preventDefault();
        window?.salesPilotPostMessages?.openLoginPage();
      }} sx={{
        color: 'text.primary'
      }}>
          Log in
        </Link>{' '}
        or{' '}
        <Link href="#" underline="always" onClick={e => {
        e.preventDefault();
        window?.salesPilotPostMessages?.openSignupPage();
      }} sx={{
        color: 'text.primary'
      }}>
          Create account
        </Link>{' '}
        to check your discount
      </Typography>
    </Stack>;
  if (isMarketablePart) {
    const ctaButtons = !!price && <CallToActionAddToCart part={part} price={price} qty={qty} stock={combinedStock} />;
    if (isMobile) {
      return <>
          <TrustBoxPriceDetailsShipWithinLabel onHandQty={onHandQty} manufacturerStock={manufacturerStock} shipProcessingDays={shipProcessingDays} />
          <PriceFields authUserCompanyName={authUserCompanyName} price={price} originalPrice={originalPrice} />
          <TrustBoxPriceDetailsMobileCallToActionBar>
            {numberInput}
            {ctaButtons}
          </TrustBoxPriceDetailsMobileCallToActionBar>
        </>;
    }
    if (isNavbarStyleOnDesktop) {
      return <TrustBoxPriceDetailsDesktopTrustBoxNavbar tenantConfig={tenantConfig} part={part} ctaButtons={ctaButtons} quantityInput={<>
              <TrustBoxPriceDetailsStockLabel stock={combinedStock} />
              {numberInput}
              <SubTotal qty={qty} price={price} />
            </>} />;
    }
    return <>
        <TrustBoxPriceDetailsShipWithinLabel onHandQty={onHandQty} manufacturerStock={manufacturerStock} shipProcessingDays={shipProcessingDays} />
        <WarrantyLabel />
        <Box sx={{
        px: 2,
        pb: 2
      }}>
          {productAndManufacturerNames}
          <PriceFields authUserCompanyName={authUserCompanyName} price={price} originalPrice={originalPrice} />
          {yourPriceCanBeLower}
          <Stack direction="row" sx={{
          justifyContent: 'space-between',
          alignItems: 'end',
          mb: 3
        }}>
            <TrustBoxPriceDetailsStockLabel stock={combinedStock} />
            {numberInput}
            <SubTotal qty={qty} price={price} />
          </Stack>
          {minOrderQtyMessage}
          {ctaButtons}
        </Box>
      </>;
  }

  // No stock info (with a price):
  if (combinedStock === 0 && !!price) {
    // console.log('No stock info:');

    const header = <>
        {!!spPartPageSetting?.inStockPrimaryMessage && <Typography variant="bodyMedium" sx={{
        mb: 2
      }}>
            {spPartPageSetting?.inStockPrimaryMessage}
          </Typography>}
      </>;
    const callToActionRequestQuote = <CallToActionRequestQuote spPartPageSetting={spPartPageSetting} shouldUseMessageFromSettings={true} price={price} part={part} qty={qty} />;
    if (isMobile) {
      return <>
          <PriceFields authUserCompanyName={authUserCompanyName} price={price} originalPrice={originalPrice} />
          {header}
          <TrustBoxPriceDetailsMobileCallToActionBar>
            {numberInput}
            {callToActionRequestQuote}
          </TrustBoxPriceDetailsMobileCallToActionBar>
        </>;
    }
    if (isNavbarStyleOnDesktop) {
      return <TrustBoxPriceDetailsDesktopTrustBoxNavbar tenantConfig={tenantConfig} part={part} ctaButtons={callToActionRequestQuote} quantityInput={<>
              {numberInput}
              <SubTotal qty={qty} price={price} />
            </>} />;
    }
    return <>
        <WarrantyLabel />
        <Box sx={{
        pl: 2,
        pr: 2,
        pb: 2
      }}>
          {productAndManufacturerNames}
          <PriceFields authUserCompanyName={authUserCompanyName} price={price} originalPrice={originalPrice} />
          {yourPriceCanBeLower}
          {header}
          <Stack direction="row" sx={{
          justifyContent: 'space-between',
          mb: 3
        }}>
            {numberInput}
            <SubTotal qty={qty} price={price} />
          </Stack>
          {callToActionRequestQuote}
        </Box>
      </>;
  }

  // No price:
  if (combinedStock > 0) {
    // console.log('No price:');

    const header = <>
        <Typography variant="h4" component="p" color="secondary" sx={{
        mb: 1,
        mt: 1
      }}>
          Get a quick Quote!
        </Typography>
        <Typography variant="bodyMedium" sx={{
        mb: 2
      }}>
          Request a quote, and we will send you price within minutes.
        </Typography>
      </>;
    const callToActionRequestQuote = <CallToActionRequestQuote spPartPageSetting={spPartPageSetting} shouldUseMessageFromSettings={false} price={price} part={part} qty={qty} />;
    if (isMobile) {
      return <>
          {header}
          <TrustBoxPriceDetailsMobileCallToActionBar>
            {numberInput}
            {callToActionRequestQuote}
          </TrustBoxPriceDetailsMobileCallToActionBar>
        </>;
    }
    if (isNavbarStyleOnDesktop) {
      return <TrustBoxPriceDetailsDesktopTrustBoxNavbar tenantConfig={tenantConfig} part={part} ctaButtons={callToActionRequestQuote} quantityInput={<>
              <TrustBoxPriceDetailsStockLabel stock={combinedStock} />
              {numberInput}
            </>} />;
    }
    return <>
        <WarrantyLabel />
        <Box sx={{
        pl: 2,
        pr: 2,
        pb: 2
      }}>
          {productAndManufacturerNames}
          {header}
          <Stack direction="row" sx={{
          justifyContent: 'space-between',
          mb: 3
        }}>
            <TrustBoxPriceDetailsStockLabel stock={combinedStock} />
            {numberInput}
          </Stack>
          {minOrderQtyMessage}
          {callToActionRequestQuote}
        </Box>
      </>;
  }

  // No price - no availability:
  // console.log('No price - no availability:');

  const header = <>
      <Typography variant="h4" component="p" color="secondary" sx={{
      mb: 1,
      mt: 1
    }}>
        Get a quick Quote!
      </Typography>
      <Typography variant="bodyMedium" sx={{
      mb: 2
    }}>
        Request a quote, and we will send you price and availability within
        minutes.
      </Typography>
    </>;
  const callToActionRequestQuote = <CallToActionRequestQuote spPartPageSetting={spPartPageSetting} shouldUseMessageFromSettings={false} price={price} part={part} qty={qty} />;
  if (isMobile) {
    return <>
        {header}
        <TrustBoxPriceDetailsMobileCallToActionBar>
          {numberInput}
          {callToActionRequestQuote}
        </TrustBoxPriceDetailsMobileCallToActionBar>
      </>;
  }
  if (isNavbarStyleOnDesktop) {
    return <TrustBoxPriceDetailsDesktopTrustBoxNavbar tenantConfig={tenantConfig} part={part} ctaButtons={callToActionRequestQuote} quantityInput={numberInput} />;
  }
  return <>
      <WarrantyLabel data-sentry-element="WarrantyLabel" data-sentry-source-file="TrustBoxPriceDetails.tsx" />
      <Box sx={{
      pl: 2,
      pr: 2,
      pb: 2
    }} data-sentry-element="Box" data-sentry-source-file="TrustBoxPriceDetails.tsx">
        {productAndManufacturerNames}
        {header}
        <Stack direction="row" sx={{
        justifyContent: 'space-between',
        mb: 3
      }} data-sentry-element="Stack" data-sentry-source-file="TrustBoxPriceDetails.tsx">
          {numberInput}
        </Stack>
        {callToActionRequestQuote}
      </Box>
    </>;
};